/**
 * The `Comparison` catalog component: peers on one rule, you marked.
 *
 * The design's answer to "is NABIL expensive?" (13a): five banks on one P/E
 * axis, cheap to dear, with the reader's own holding drawn larger and in its
 * own hue — the design says brand green there and this surface does not, for
 * the reason recorded at the dot. What the strip says that a table does not
 * is where a company sits BETWEEN the others — a column of five numbers makes
 * the reader do that arithmetic themselves.
 *
 * THE POSITION ARRIVES ON THE WIRE. It is derived on the server from the
 * measure's own spread, the way a bar's height is, and this file never
 * computes one: two surfaces deriving the same coordinate is two surfaces
 * that can disagree about where a company sits.
 *
 * On a wide panel labels stagger onto the two rows given by `row`. In the
 * narrow rail each peer gets a separate line, keeping nearby marks readable
 * while retaining the server's position and the stated average.
 *
 * `mine` IS THE ROW'S OWN WORD, never inferred here. A strip drawn from a
 * tool that does not say which holding is the reader's marks nobody, which
 * is the honest picture — a guess would put a green dot on someone else's
 * company.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import { captionLabel } from '@/features/mitra/lib/figures'
import { Fig, Label } from '@/features/mitra/panel/blocks/Card'
import { assigner, fill } from '@/features/mitra/panel/blocks/shapes/identity'
import { useLabelLang } from '@/features/mitra/panel/blocks/charts/labelLang'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'
import { copy, type Lang } from '@/features/mitra/lib/copy'

export const ComparisonApi = {
  name: 'Comparison',
  schema: z
    .object({
      marks: PathBinding,
      // The engine's `tick` parts: the rule's two ends and the group's own
      // mark (`streaming/catalog.py`). Only the group mark is read here.
      ticks: PathBinding.optional(),
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/marks`, fixed by `to_messages`. */
export type MarkPart = {
  id: string
  label: string
  value: CellValue
  /** Where along the rule, 0-100. Derived server-side; see the note above. */
  position: number
  mine: boolean
  /** "upper" | "lower" for a peer; "average" or "median" for the group's own mark. */
  row: string
}

/** The shape behind `/ticks`: `id` is "low", "high" or the group mark's. */
export type TickPart = { id: string; label: string; value: CellValue; position: number }

/**
 * THE GROUP'S OWN MARK, NEVER A COMPANY. A mean ("average") or, since
 * 2026-10-05, the middle value ("median": one outlier dragged a peer
 * group's mean to where no company sat, wow retest 2 A2). Either is drawn as
 * the tick, and counting it as a peer would draw a company called MEDIAN.
 */
const GROUP_ROLES = new Set(['average', 'median'])

/** What the tick is called: the engine's own word for it, else the register's. */
function groupWord(mark: { id: string; label: string; row?: string }, lang: Lang): string {
  const label = mark.label.trim()
  if (label && label !== mark.id) return label
  const words = copy(lang)
  return mark.row === 'median' || mark.id === 'median' ? words.groupMedian : words.groupAverage
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const comparisonApi = ComparisonApi as unknown as ComponentApi

export const Comparison = createComponentImplementation<ComponentApi>(
  comparisonApi,
  ({ props }: { props: { marks?: unknown; ticks?: unknown; caption?: unknown } }) => (
    <ComparisonStrip
      marks={listOf<MarkPart>(props.marks)}
      ticks={listOf<TickPart>(props.ticks)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The rule's height, in px: two label rows and the line between them. */
const RULE_H = 66

/** The drawing, given the list the surface resolved. */
export function ComparisonStrip({
  marks,
  ticks = [],
  caption,
}: {
  marks: MarkPart[]
  ticks?: TickPart[]
  caption: string | null
}) {
  const lang = useLabelLang()
  const peers = marks.filter((m) => !GROUP_ROLES.has(m.row))
  const marked = marks.find((m) => GROUP_ROLES.has(m.row))
  const ticked = ticks.find((t) => GROUP_ROLES.has(t.id))
  const average = marked
    ? { ...marked, word: groupWord(marked, lang) }
    : ticked
      ? { ...ticked, word: groupWord(ticked, lang) }
      : null
  if (peers.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  // ONLY THE READER'S OWN COMPANIES ARE IDENTIFIED, so only they are what
  // `assign` is given. Everything else on this strip is a peer — a member of
  // the group the measure is read against — and "not individually identified"
  // is exactly what `"rest"` says, so `hueOf` returns it for a peer without
  // this file naming a colour for one. Called once, and nothing here sorts;
  // the label is the symbol, which is the key `shapes/Comparison` uses for the
  // same company, so the two renderings of one envelope cannot disagree.
  const hueOf = assigner(peers.filter((m) => m.mine).map((m) => m.label))

  return (
    <figure
      data-role="chart"
      data-component="comparison"
      className="agent-comparison m-0 flex flex-col gap-2 px-3 pb-3 pt-3"
    >
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      {/* INSET, so a mark at 0 or 100 keeps its label on the card. The rule
          spans the full width; the marks sit inside it. */}
      <div
        data-role="comparison-axis"
        className="relative mx-3"
        style={{ height: `${String(RULE_H)}px` }}
      >
        <i
          data-role="rule"
          className="absolute left-0 right-0 block h-px bg-line"
          style={{ top: `${String(RULE_H / 2)}px` }}
        />
        {average && (
          <span
            data-role="average"
            className="absolute flex -translate-x-1/2 flex-col items-center gap-1"
            style={{ left: `${String(average.position)}%`, top: `${String(RULE_H / 2 - 7)}px` }}
          >
            <i className="block h-4 w-px bg-ink-soft/60" />
            <span className="whitespace-nowrap font-mono text-mono-s text-ink-soft">
              {average.word} <Fig cell={average.value} quietUnit className="text-mono-s" />
            </span>
          </span>
        )}
        {peers.map((m) => {
          const upper = m.row === 'upper'
          return (
            <span
              key={m.id}
              data-role="mark"
              data-mine={m.mine ? 'true' : 'false'}
              className={`absolute flex -translate-x-1/2 items-center gap-1
                          ${upper ? 'flex-col-reverse' : 'flex-col'}`}
              style={{
                left: `${String(m.position)}%`,
                // The dot sits on the rule either way; the label goes above
                // or below it, so the two rows are the two label positions
                // rather than two different heights for the dot.
                top: upper ? `${String(RULE_H / 2 - 26)}px` : `${String(RULE_H / 2 - 5)}px`,
              }}
            >
              <i
                // YOURS IS BIGGER, which is 13j's rule — "your holding is
                // always marked". The ring is the card's own fill, so a dot
                // overlapping the rule still reads as a dot.
                //
                // AND IT IS NO LONGER GREEN. The old note read: "`brand-deep`,
                // not `brand`: this dot IS the meaning rather than a ground
                // something else is read on, and WCAG 1.4.11 asks 3:1 of an
                // object that is nothing but its colour. The light brand
                // clears that as a background and not as a glyph.
                // `embed-theme.test.ts` enumerates every site and caught this
                // one within the hour it was written." Every word of that
                // still holds of `brand-deep`; what changed is that the brand
                // is the wrong CHANNEL here. A literal accent makes every
                // subject on every card one colour — a hue meaning "this is
                // the one you asked about" rather than "this is NABIL" — and
                // `shapes/Comparison.tsx` draws this same envelope with the
                // company's own hue. Two renderings of one answer must not
                // disagree about what colour a company is, so this takes
                // `identity.ts` too: the reader's own is the hue it wore on
                // their positions card, and a peer is the tail. The size
                // stays, because colour is never the only channel (1.4.1),
                // and both halves of the pair are measured against the card
                // in `embed-theme.test.ts` the same way `brand-deep` was.
                className={`block rounded-full border-2 border-surface
                            ${m.mine ? 'h-4 w-4' : 'h-2 w-2'} ${fill(hueOf(m.label))}`}
              />
              <span
                // THE LABEL IS AN IDENTIFIER AND TAKES NO HUE. `identity.ts`
                // is explicit that a coloured ticker reads as a link, and two
                // colours on one mark — a hue on the dot, the brand on the
                // word beside it — would say the dot and the label were two
                // different claims. Weight of ink carries "yours" here.
                className={`whitespace-nowrap font-mono text-mono-s tabular-nums
                            ${m.mine ? 'text-ink' : 'text-ink-soft'}`}
              >
                {m.label} <Fig cell={m.value} quietUnit className="text-mono-s" />
              </span>
            </span>
          )
        })}
      </div>
      <div data-role="comparison-list" className="flex flex-col gap-2">
        {average && (
          <p className="m-0 border-b border-rule pb-2 text-mono-s font-medium text-ink-soft">
            {average.word} <Fig cell={average.value} quietUnit className="text-body-xs text-ink" />
          </p>
        )}
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {peers.map((m) => (
            <li
              key={m.id}
              data-role="comparison-peer"
              data-mine={m.mine ? 'true' : 'false'}
              className={`min-w-0 rounded-block px-2 py-2 ${m.mine ? 'bg-ground' : ''}`}
            >
              <div className="flex min-w-0 items-baseline justify-between gap-3">
                <span
                  className={`min-w-0 break-words font-mono text-body-xs ${
                    m.mine ? 'font-bold text-ink' : 'text-ink-mid'
                  }`}
                >
                  {m.label}
                </span>
                <Fig cell={m.value} quietUnit className="shrink-0 text-body-s text-ink" />
              </div>
              <div className="relative mx-2 mt-2 h-2" aria-hidden="true">
                <i className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-edge" />
                {average && (
                  <i
                    className="absolute top-0 h-2 w-px bg-ink-soft"
                    style={{ left: `${String(average.position)}%` }}
                  />
                )}
                <i
                  data-role="comparison-peer-dot"
                  // h-3/w-3, not the down-rounded h-2 non-mine already uses:
                  // "yours is bigger" (13j) is a meaningful distinction, not
                  // decoration, so the tie snaps up to keep the two sizes apart.
                  className={`absolute top-0 -translate-x-1/2 rounded-full border border-surface ${
                    m.mine ? 'h-3 w-3 -translate-y-px' : 'h-2 w-2'
                  } ${fill(hueOf(m.label))}`}
                  style={{ left: `${String(m.position)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  )
}
